import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { FunctionComponent, ReactElement, useEffect, useRef } from 'react';
import { scroller } from 'react-scroll';

import { PaletteColor, Size } from '../../styling/AppStyles';
import { spaceBetween } from '../../styling/mixins';
import ExinStepIcon from './ExinStepIcon';
import ExinText from './ExinText';

export type Step = {
  key: string;
  label: ReactElement;
  content: ReactElement;
};

const ExinStepContainer = styled.div<{ isFocused: boolean }>`
  display: flex;
  flex-direction: row;
  background: ${PaletteColor.highlightBackground};
  border-radius: 20px;
  padding: ${Size.small};

  ${({ isFocused: isActive }) =>
    isActive
      ? css`
          border: 2px solid ${PaletteColor.text};
        `
      : css`
          border: 1px solid transparent;
        `}
`;

const ExinStepBody = styled.div`
  flex: 1 1 auto;
  margin-left: ${Size.smaller};
  ${spaceBetween(Size.smaller)};
`;

const ExinStep: FunctionComponent<{
  number: number;
  label: string | ReactElement;
  content: string | ReactElement;
  /**
   * When true, this step gets a checkmark
   */
  isCompleted: boolean;
  /**
   * When true, this step's contents is visible. Per design this is always true for steps
   * that are already completed.
   */
  isActive: boolean;
  /**
   * When true, this step is given a distinctive color. When false, it is somewhat dimmed.
   * Per design the steps that are highlighted should normally also be active.
   */
  isHighlighted: boolean;
}> = ({ label, number, content, isCompleted, isActive, isHighlighted }) => {
  const domIdentifier = useRef(`step-container-${number}-${Math.round(1000 * Math.random())}`);
  const wasHighlighted = useRef(isHighlighted);
  const header = document.getElementsByClassName('mainnav').item(0);
  const headerOffset =
    (header ? header.getBoundingClientRect().height + header.getBoundingClientRect().top : 0) + 20;

  useEffect(() => {
    if (isHighlighted && !wasHighlighted.current) {
      // The step has become highlighted just now
      scroller.scrollTo(domIdentifier.current, {
        delay: 50,
        duration: 800,
        offset: -headerOffset,
        smooth: 'easeInOutQuart',
      });
    }
    wasHighlighted.current = isHighlighted;
  }, [headerOffset, isHighlighted]);

  return (
    <ExinStepContainer id={domIdentifier.current} isFocused={isHighlighted}>
      <ExinStepIcon
        isCompleted={isCompleted}
        isHighlighted={isCompleted || isHighlighted}
        number={number}
      />
      <ExinStepBody>
        <ExinText>{label}</ExinText>
        {isActive && content}
      </ExinStepBody>
    </ExinStepContainer>
  );
};

const ExinStepperContainer = styled.section`
  ${spaceBetween(Size.medium)};
`;

const ExinStepper: FunctionComponent<{
  steps: Array<Step>;
  activeStep: number;
}> = ({ steps, activeStep }) => {
  return (
    <ExinStepperContainer>
      {steps.map((step, index) => (
        <ExinStep
          key={step.key}
          number={index + 1}
          label={step.label}
          content={step.content}
          isCompleted={index < activeStep}
          isActive={index <= activeStep}
          isHighlighted={index === activeStep}
        />
      ))}
    </ExinStepperContainer>
  );
};

export default ExinStepper;
